import { useState } from 'react';
import { useAppStore } from '../store';
import { ERROR_CATEGORY_LABELS, ERROR_CATEGORY_COLORS } from '../types';
import type { FavoriteItem } from '../types';
import { exportFavoritesToPdf } from '../utils/favoritesPdf';
import { Trash2, Download, BookOpen, Star, FileText, Inbox, Loader2 } from 'lucide-react';

export default function CollectionPage() {
  const { favorites, removeFromFavorites } = useAppStore();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const dateStr = new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
      await exportFavoritesToPdf(
        favorites,
        'Refine-语法错题集.pdf',
        '语法错题集',
        `共 ${favorites.length} 个错误 · 导出于 ${dateStr}`
      );
    } catch (error) {
      console.error('PDF export failed:', error);
      alert('PDF导出失败，请重试。');
    } finally {
      setIsExporting(false);
    }
  };

  // Group by category
  const grouped: Record<string, typeof favorites> = {};
  favorites.forEach(fav => {
    const cat = fav.error.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(fav);
  });

  return (
    <div className="min-h-screen bg-surface py-10 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="font-serif text-3xl sm:text-4xl text-primary mb-1 flex items-center gap-3">
              <BookOpen size={32} className="text-accent" />
              错题集
            </h1>
            <p className="text-slate-500">
              已收藏 {favorites.length} 条错误 · 你的个人语法错题本
            </p>
          </div>
          {favorites.length > 0 && (
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="group inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  导出中...
                </>
              ) : (
                <>
                  <Download size={18} />
                  导出 PDF
                </>
              )}
            </button>
          )}
        </div>

        {/* Empty state */}
        {favorites.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <Inbox size={36} className="text-slate-300" />
            </div>
            <h2 className="font-serif text-2xl text-primary mb-2">还没有收藏的错误</h2>
            <p className="text-slate-500 max-w-md mx-auto mb-2">
              你在练习中改对的句子会自动保存在这里。
            </p>
            <p className="text-sm text-slate-400">
              你也可以在分析页手动点击星标收藏。
            </p>
          </div>
        )}

        {/* Grouped errors */}
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: ERROR_CATEGORY_COLORS[category as keyof typeof ERROR_CATEGORY_COLORS] }}
              />
              <h2 className="font-serif text-xl text-primary">
                {ERROR_CATEGORY_LABELS[category as keyof typeof ERROR_CATEGORY_LABELS]}
              </h2>
              <span className="text-xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                {items.length}
              </span>
            </div>

            <div className="space-y-3">
              {items.map(fav => (
                <div
                  key={fav.error.id}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 hover:shadow-md transition-shadow group"
                >
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                      <div className="text-xs font-bold text-red-400 mb-1.5 tracking-wider">原句 ✗</div>
                      <p className="text-sm text-red-800 leading-relaxed line-through decoration-red-300">{fav.error.originalSentence}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                      <div className="text-xs font-bold text-emerald-400 mb-1.5 tracking-wider">修改后 ✓</div>
                      <p className="text-sm text-emerald-800 leading-relaxed font-medium">{fav.error.correctedSentence}</p>
                    </div>
                  </div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm text-slate-500 leading-relaxed">{fav.error.explanationZh || '暂无中文解析'}</p>
                      <div className="flex items-center gap-3 mt-3">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <FileText size={10} />
                          {fav.error.essayTitle}
                        </span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Star size={10} />
                          收藏于 {new Date(fav.addedAt).toLocaleDateString('zh-CN')}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromFavorites(fav.error.id)}
                      className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="移出错题集"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
